import { ClipboardList, Pause, Play, RotateCcw } from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { mensajeDe } from '@/api/errors';
import { BotonBiColor, TituloBloque } from '@/components/BloquesConsalud';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { AVATARES } from '@/lib/avatares';
import { useStagger } from '@/lib/motion';
import { cn } from '@/lib/utils';

import { Cargando, ErrorCarga, TabsModulo } from '../curso/componentes';

import { colorCalificacion, mensajeCalificacion } from './comun';
import { useExamenes, useIniciarExamen, type ExamItem } from './queries';

const ESTADO: Record<ExamItem['status'], string> = {
  not_started: 'Sin resolver',
  in_progress: 'Incompleto',
  paused: 'En pausa',
  finished: 'Completo',
};
// Colores del estatus en el legacy: gris, amarillo, rojo (pausa) y verde.
const COLOR_ESTADO: Record<ExamItem['status'], string> = {
  not_started: 'text-texto-3',
  in_progress: 'text-amarillo-texto',
  paused: 'text-rojo-texto',
  finished: 'text-verde-texto',
};

/** «TIPS CONSALUD» de `examenes-prime/empezar-examen`, con sus íconos originales. */
const TIPS = [
  {
    titulo: 'Aléjate de tus distracciones',
    texto: 'Así tu concentración, desempeño y aprovechamiento serán óptimos.',
    icono: '/img/examenes/tip-icon1.png',
  },
  {
    titulo: 'Mide tus tiempos',
    texto:
      'Te recomendamos responder cada pregunta en menos de 1 minuto, ya que es el tiempo máximo que se recomienda emplear por pregunta en el ENARM. Lo ideal es que te habitúes a contestar en 45 segundos.',
    icono: '/img/examenes/tip-icon2.png',
  },
  {
    titulo: 'Revisa tu progreso',
    texto:
      'Al finalizar podrás consultar tu calificación general y por área (Ginecología, Pediatría, Medicina Interna y Cirugía) para establecer tus fortalezas y áreas de oportunidad.',
    icono: '/img/examenes/tip-icon3.png',
  },
];

/** Exámenes (decisión D16: flujo prime). Pestañas Exámenes / Simulacros (400+ preguntas). */
export function ExamenesPage() {
  const [params, setParams] = useSearchParams();
  const tipo = params.get('tipo') === 'simulacros' ? 'drills' : 'exams';
  const { data, error, isPending } = useExamenes(tipo);
  const iniciar = useIniciarExamen();
  const navigate = useNavigate();
  const [reglas, setReglas] = useState<{ e: ExamItem; restart: boolean } | null>(null);
  const root = useRef<HTMLDivElement>(null);
  useStagger(root, '[data-fila]', { step: 30, y: 10, deps: [data] });

  const empezar = (e: ExamItem, restart: boolean) => {
    iniciar.mutate(
      { id: e.id, restart },
      {
        onSuccess: (r) =>
          void navigate(
            r.status === 'finished'
              ? `/curso-enarm/examenes-prime/intento/${String(r.id)}/resultados`
              : `/curso-enarm/examenes-prime/intento/${String(r.id)}`,
          ),
        onError: (er) => toast.error(mensajeDe(er)),
      },
    );
  };

  const accion = (e: ExamItem) => {
    if (e.status === 'not_started') setReglas({ e, restart: false });
    else empezar(e, false);
  };

  return (
    <div ref={root} className="space-y-6">
      <TabsModulo />
      <h1 className="titulo-seccion">
        <span className="subrayado-verde">Exámenes</span>
      </h1>
      <section className="panel-vidrio space-y-5 pt-14">
        <TituloBloque avatar={AVATARES.doctora} icono={<ClipboardList size={18} />}>
          {tipo === 'exams' ? 'Exámenes' : 'Simulacros'}
        </TituloBloque>
        <div className="flex flex-wrap justify-center gap-2">
          {(['exams', 'drills'] as const).map((t) => (
            <button
              key={t}
              type="button"
              className="btn-modulo"
              data-state={tipo === t ? 'active' : undefined}
              onClick={() => setParams(t === 'drills' ? { tipo: 'simulacros' } : {})}
            >
              {t === 'exams' ? 'Exámenes' : 'Simulacros'}
            </button>
          ))}
        </div>
        {isPending && <Cargando />}
        {error && <ErrorCarga error={error} />}
        {data && data.items.length === 0 && (
          <p className="text-center text-texto-2">
            No hay {tipo === 'exams' ? 'exámenes' : 'simulacros'} disponibles para tu licencia.
          </p>
        )}
        {data && data.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="tabla-cs tabla-cs-verde">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Examen</th>
                  <th>Modo</th>
                  <th>Categoría</th>
                  <th>Preguntas</th>
                  <th>Calificación</th>
                  <th>Promedio global</th>
                  <th>Estatus</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.items.map((e, i) => (
                  <tr key={e.id} data-fila>
                    <td className="tabular">{i + 1}</td>
                    <td className="font-bold text-texto">{e.name}</td>
                    <td className="uppercase">{e.mode === 'enarm' ? 'ENARM' : 'Entrenamiento'}</td>
                    <td className="uppercase">{e.category}</td>
                    <td className="tabular">{e.questions}</td>
                    <td>
                      {e.score !== null ? (
                        <span
                          className={cn('font-extrabold tabular', colorCalificacion(e.score))}
                          title={`Aciertos ${String(e.correct)} de ${String(e.total)}`}
                        >
                          {e.score}%{' '}
                          <span className="text-xs font-bold">{mensajeCalificacion(e.score)}</span>
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="tabular">
                      {e.globalAverage !== null ? `${String(e.globalAverage)}%` : '—'}
                    </td>
                    <td className={cn('font-bold whitespace-nowrap', COLOR_ESTADO[e.status])}>
                      {e.status === 'paused' && <Pause size={12} className="mr-1 inline" />}
                      {ESTADO[e.status]}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      <button
                        type="button"
                        className="btn-cs btn-primario"
                        disabled={iniciar.isPending}
                        onClick={() => accion(e)}
                      >
                        <Play size={14} />
                        {e.status === 'finished'
                          ? 'Resultados'
                          : e.status === 'not_started'
                            ? 'Empezar'
                            : 'Reanudar'}
                      </button>
                      {e.status !== 'not_started' && (
                        <button
                          type="button"
                          className="btn-cs btn-vidrio ml-2"
                          aria-label={`Reiniciar ${e.name}`}
                          title="Volver a resolver"
                          onClick={() => setReglas({ e, restart: true })}
                        >
                          <RotateCcw size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Dialog
        open={reglas !== null}
        onOpenChange={(o) => {
          if (!o) setReglas(null);
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <p className="text-xs text-texto-3">Nombre del examen</p>
          <DialogTitle className="text-azul-texto">{reglas?.e.name}</DialogTitle>
          <DialogDescription>
            {reglas?.restart
              ? 'Vas a volver a resolver este examen: se borrará tu intento anterior.'
              : 'Antes de empezar:'}
          </DialogDescription>
          <div className="mt-8 grid gap-6 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            {/* Reglas de aplicación (tarjeta con el cerebro asomado, como el legacy) */}
            <section className="card-cerebro space-y-3 text-sm">
              <img
                src="/img/isotipo-cerebro-azul.png"
                alt=""
                className="card-cerebro__isotipo solo-claro"
              />
              <img
                src="/img/isotipo-cerebro-blanco.png"
                alt=""
                className="card-cerebro__isotipo solo-oscuro"
              />
              <h3 className="text-center text-base font-extrabold text-azul-texto">
                Reglas de aplicación del examen
              </h3>
              <ul className="list-disc space-y-1 pl-5 text-texto-2">
                <li>
                  {reglas?.e.questions} preguntas. Calcula 1 minuto por caso clínico:{' '}
                  <strong className="text-texto">máximo {reglas?.e.questions} min</strong>.
                </li>
                <li>
                  {reglas?.e.mode === 'enarm'
                    ? 'Modo ENARM: verás las respuestas correctas al terminar.'
                    : 'Modo entrenamiento: confirma cada caso para ver la respuesta y su justificación.'}
                </li>
                <li>Tu avance se guarda solo; puedes pausar y reanudar cuando quieras.</li>
              </ul>
              <p className="text-center font-extrabold text-rojo-texto">¡MUCHO ÉXITO!</p>
            </section>
            {/* Tips Consalud */}
            <section className="space-y-4">
              <h3 className="text-center font-extrabold text-azul-texto">TIPS CONSALUD</h3>
              {TIPS.map((t) => (
                <div key={t.titulo} className="relative">
                  <p className="rounded-[10px] border-2 border-azul-600 py-1.5 pr-12 pl-3 text-center text-sm font-extrabold text-azul-texto">
                    {t.titulo}
                  </p>
                  <img
                    src={t.icono}
                    alt=""
                    className="absolute -top-3 -right-2 h-11 w-11 rounded-full bg-white p-1.5 shadow"
                  />
                  <p className="mt-1 px-1 text-xs font-bold text-texto-2">{t.texto}</p>
                </div>
              ))}
            </section>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            <button type="button" className="btn-cs btn-vidrio" onClick={() => setReglas(null)}>
              Cancelar
            </button>
            <BotonBiColor
              icono={<Play size={24} />}
              variante="verde"
              className="mx-0 w-[260px]"
              disabled={iniciar.isPending}
              onClick={() => {
                if (reglas) empezar(reglas.e, reglas.restart);
                setReglas(null);
              }}
            >
              {reglas?.restart ? 'Volver a resolver' : 'Comenzar'}
            </BotonBiColor>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
